import { AddPrimitivesCommand, BasicControl, CommandGroup, CURSOR_TYPE, dymode, DYMODE_MODE, Listeners, MOUSE, Plane, Point, Polygon, RevolveSolidT, StraightLine, TnEngineContext, TnEngineExtraContext, ViewEditor } from "pytha";
import { HVAC_CTRL_TYPE } from "../enums/hvac-control-type";
import { Vector3 } from "three";
import { HVAC_SIGNAL } from "../enums/hvac-signal";
import { TunnelT, UpdateTunnelCommand } from "tnbimbase";
import { graphicStore } from "@/commons/store/graphic-store";

export class AddHvacTunnelControl extends BasicControl {

    id = HVAC_CTRL_TYPE.ADD_HVAC_TUNNEL;

    point_down: Point = new Point();
    point_move: Point = new Point();
    capturePoint: Vector3;
    orthogonalPoint: Vector3;
    polarPoint: Vector3;
    step: number = 1;

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    helpExtrudeSolid: TunnelT; // 管道的临时图元
    helpRevolveSolid: RevolveSolidT; // 连接管的临时图元

    // 第一个点坐标
    firstPoint: Vector3 = new Vector3();
    // 第二个点坐标
    secondPoint: Vector3 = new Vector3();

    componentData: any;
    bottomFace: Polygon;
    diameter: number; // 管径

    ex: number = 0;
    ey: number = 0;
    ez: number = 0;
    angle: number;
    offset: number;

    workPlane: Plane = new Plane(); // 计算了标高偏移后的工作面

    lastExtrudeSolid: TunnelT; // 记录上一段管道，如有连接管，需要截取上一段管道

    dist: number = 0; // 上一段管道因接入连接管要截取的长度

    tunnelAngle: number = 0; // 管道连接的旋转体的旋转角度
    layerUuid: string = ""; // 管道所在图层

    private boundInitControl: Function;

    constructor(editor: ViewEditor, listeners: Listeners) {
        super();
        this.editor = editor;
        this.listeners = listeners;

        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();

        this.boundInitControl = this.initControl.bind(this);
        // 布置管道初始信号
        this.listeners.getSignal(HVAC_SIGNAL.initHvacTunnelControl).add(this.boundInitControl);
        this.needInit = true;
        console.log('AssembleTunnelControl added')
    }

    dispose(): void {
        // 移除监听事件
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.listeners.getSignal(HVAC_SIGNAL.initHvacTunnelControl).remove(this.boundInitControl);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        this.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.selectControl.setEnabled(true);
        this.editor.clampControl.enabled = true;
        this.editor.clampControl.hidden = false;

        if (this.lastExtrudeSolid && this.lastExtrudeSolid.viewObj && this.lastExtrudeSolid.viewObj.parent) {
            this.lastExtrudeSolid.viewObj.parent?.remove(this.lastExtrudeSolid.viewObj);
        }
        if (this.helpExtrudeSolid && this.helpExtrudeSolid.viewObj && this.helpExtrudeSolid.viewObj.parent) {
            this.helpExtrudeSolid.viewObj.parent?.remove(this.helpExtrudeSolid.viewObj);
        }
        if (this.helpRevolveSolid && this.helpRevolveSolid.viewObj && this.helpRevolveSolid.viewObj.parent) {
            this.helpRevolveSolid.viewObj.parent?.remove(this.helpRevolveSolid.viewObj);
        }

        console.log('AssembleTunnelControl removed')
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            if (this.step == 1) {
                // 获得了第一个点
                this.firstPoint.set(value.x, value.y, value.z);
                this.firstStep();
            } else if (this.step == 2) {
                // 获得了第二个点
                this.secondPoint.set(value.x, value.y, value.z);
                this.secondStep();
            }
        } else if (value.mode === DYMODE_MODE.DIST) {
            if (this.step == 2) {
                this._dir = this.point_move.clone().sub(this.firstPoint).normalize();
                let end = this._dir.multiplyScalar(value.dist).add(this.firstPoint);
                this.secondPoint.set(end.x, end.y, end.z);
                this.secondStep();
            }
        }
    }

    firstStep = () => {
        // 获得了第一个点坐标
        // 监听正交、极轴点
        this.listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
        this.listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
        this.listeners.signals.needCapture.dispatch(this.firstPoint);
        this.listeners.signals.orthogonal.dispatch(this.firstPoint);
        this.listeners.signals.polarTracking.dispatch(this.firstPoint);
        // 构造临时拉伸体
        this.generateSolid(this.context.ucsContext.getAxisList().axisZ, this.firstPoint, 1);
        // 发送指定第二个点的信号
        this.listeners.signals.sendToDymodeTip.dispatch('指定下一个点:');
        this.extraContext.stmodeContext.setStmode('指定下一个点', []);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.DIST, this.firstPoint);
        this.step = 2;
        // 监听鼠标移动事件
        this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
    }

    secondStep = () => {
        this._dir.set(this.secondPoint.x - this.firstPoint.x, this.secondPoint.y - this.firstPoint.y, this.secondPoint.z - this.firstPoint.z);
        this.update();
        if (this.lastExtrudeSolid != null) {
            let command = new CommandGroup();
            // 不是第一段管道
            if (this.dist < this.lastExtrudeSolid._geo.height && this.dist != 0) {
                // 更新上一段管道
                let updateTunnelCommand = new UpdateTunnelCommand(this.lastExtrudeSolid, this.tunnelAngle, this.lastExtrudeSolid._geo.height - this.dist)
                command.cmds.push(updateTunnelCommand)
            }
            // 添加连接管
            if (this.helpRevolveSolid != null) {
                let entity = this.helpRevolveSolid.clone();
                entity.layerUuid = this.layerUuid;
                let addConnectTunnelCommand = new AddPrimitivesCommand([entity]);
                command.cmds.push(addConnectTunnelCommand);
            }
            // 添加管道
            // this.lastExtrudeSolid = this.helpExtrudeSolid.clone();
            this.lastExtrudeSolid = new TunnelT(this.helpExtrudeSolid._geo.bottomFace.clone(), this.helpExtrudeSolid._geo.direction.clone(), this.helpExtrudeSolid._geo.height)
            this.lastExtrudeSolid._geo.oriAngle = this.tunnelAngle
            this.lastExtrudeSolid._geo.extPoint = new Point().copy(this.secondPoint)
            // this.lastExtrudeSolid.materialUuid = this.componentData.materialUuid;
            this.lastExtrudeSolid.layerUuid = this.layerUuid;
            let addTunnelCommand = new AddPrimitivesCommand([this.lastExtrudeSolid]);
            command.cmds.push(addTunnelCommand);
            this.editor.history.execute(command);
        } else {
            // 是第一段管道
            this.insertComponent();
            // 渲染连接管
            this.generateConnectSolid();
        }
        this.firstPoint.copy(this.secondPoint);
        if (this.helpExtrudeSolid && this.helpExtrudeSolid.viewObj && this.helpExtrudeSolid.viewObj.parent) {
            this.helpExtrudeSolid.viewObj.parent?.remove(this.helpExtrudeSolid.viewObj);
        }
        this.generateSolid(this.context.ucsContext.getAxisList().axisZ, this.firstPoint, 1);
        this.listeners.signals.needCapture.dispatch(this.firstPoint);
        this.listeners.signals.orthogonal.dispatch(this.firstPoint);
        this.listeners.signals.polarTracking.dispatch(this.firstPoint);
        // 发送指定第二个点的信号
        this.listeners.signals.sendToDymodeTip.dispatch('指定下一个点:');
        this.extraContext.stmodeContext.setStmode('指定下一个点', []);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.DIST, this.firstPoint);
        this.step = 2;
    }

    initControl = (data: any) => {
        this.onAssembleTunnel(data, data.bottomFace, data.diameter, data.offset)
    }

    onAssembleTunnel = (componentData: any, bottomFace: Polygon, diameter: number, offset: number) => {
        this.step = 1;
        // 记录管道信息
        this.componentData = componentData;
        this.bottomFace = bottomFace;
        this.diameter = diameter;
        this.offset = offset;
        this.layerUuid = componentData.layerUuid;
        let onePoint = graphicStore.context.ucsContext.currentUcs.origin.clone();
        onePoint.addScaledVector(graphicStore.context.ucsContext.currentUcs.axisZ, this.offset);
        this.workPlane.setFromNormalAndCoplanarPoint(graphicStore.context.ucsContext.currentUcs.axisZ, onePoint);
        // 派发需要捕捉
        this.listeners.signals.needCapture.dispatch();
        // 监听鼠标按下事件
        this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        // 监听捕捉点
        this.listeners.signals.onPointCapture.add(this.onPointCapture);
        // 监听动态输入
        this.listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        // 置空选择集
        this.listeners.signals.emptySelectedEntities.dispatch();
        // 修改光标
        this.listeners.signals.onCursorChange.dispatch(CURSOR_TYPE.CROSS);
        // 钝化选择控制器
        this.editor.selectControl.setEnabled(false);
        this.editor.clampControl.enabled = false;
        this.editor.clampControl.hidden = true;
        // 派发指定第一个点的信号
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.POINT);
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        this.listeners.signals.sendToDymodeTip.dispatch('指定第一个点:');
        this.extraContext.stmodeContext.setStmode('指定第一个点', []);
        this.listeners.signals.setInitControlData.dispatch({...componentData})
    }

    // 添加构件
    insertComponent = () => {
        // this.lastExtrudeSolid = this.helpExtrudeSolid.clone()  ;
        this.lastExtrudeSolid = new TunnelT(this.helpExtrudeSolid._geo.bottomFace.clone(), this.helpExtrudeSolid._geo.direction.clone(), this.helpExtrudeSolid._geo.height)
        this.lastExtrudeSolid._geo.extPoint = new Point().copy(this.secondPoint)
        // this.lastExtrudeSolid.materialUuid = this.componentData.materialUuid;
        this.lastExtrudeSolid.layerUuid = this.layerUuid;
        this.editor.history.execute(new AddPrimitivesCommand([this.lastExtrudeSolid]));
    }

    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT) {
            this.extraContext.listeners.signals.onOpeCommandControlEnd.dispatch();
        }
        if (event.button !== MOUSE.LEFT) return;
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.step == 1) {
            // 光标投影在工作面上的点
            this.point_down = graphicStore.extraContext.mousePosContext.viewCursorRay.intersectPlane(this.workPlane);
            // 获得了第一个点坐标
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.firstPoint = this.point_down.clone();
            this.firstStep();
        } else if (this.step == 2) {
            // 光标投影在工作面上的点
            this.point_down = graphicStore.extraContext.mousePosContext.viewCursorRay.intersectPlane(this.workPlane);
            // 获得了第二个点坐标
            if (this.orthogonalPoint) {
                this.point_down.copy(this.orthogonalPoint);
            }
            if (this.polarPoint) {
                this.point_down.copy(this.polarPoint);
            }
            if (this.capturePoint) {
                this.point_down.copy(this.capturePoint);
            }
            this.secondPoint = this.point_down.clone();
            this.secondStep();
        }
    }

    private _dir = new Vector3();
    private _tmpAxisDir = new Vector3();
    onPointerMove = (event) => {
        if (this.step != 2) return;
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        // 光标投影在工作面上的点
        this.point_move = graphicStore.extraContext.mousePosContext.viewCursorRay.intersectPlane(this.workPlane);
        if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        }
        if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        }
        // 改变当前管道轴向和轴向长度
        this._dir.set(this.point_move.x - this.firstPoint.x, this.point_move.y - this.firstPoint.y, this.point_move.z - this.firstPoint.z);
        this.update();
    }

    private _moveDir = new Vector3();
    update = () => {
        this._moveDir.subVectors(this.firstPoint, this.helpExtrudeSolid._geo.bottomFace.basePoint);
        this.helpExtrudeSolid.moveNoMovePoint(this._moveDir.clone().normalize(), this._moveDir.length());
        let normalRotateAxis = this.helpExtrudeSolid._geo.direction.clone().cross(this._dir).normalize();
        let normalDeltaAngle = this.helpExtrudeSolid._geo.direction.angleTo(this._dir);
        // 底面旋转至相应的角度
        if (normalRotateAxis.length() > 1e-6) {
            this.helpExtrudeSolid._geo.rotate(this.firstPoint as Point, normalRotateAxis, normalDeltaAngle);
        }
        this.helpExtrudeSolid._geo.height = this._dir.length();
        let newAxisX = this.context.ucsContext.getAxisList().axisX.clone().projectOnPlane(this._dir);
        if (newAxisX.length() < 1e-6) {
            newAxisX = this.context.ucsContext.getAxisList().axisY.clone().projectOnPlane(this._dir);
        }
        let axisXRotateAxis = this.helpExtrudeSolid._geo.bottomFace.axisX.clone().cross(newAxisX).normalize();
        let axisXDeltaAngle = this.helpExtrudeSolid._geo.bottomFace.axisX.angleTo(newAxisX);
        if (Math.abs(this.helpExtrudeSolid._geo.bottomFace.axisX.dot(newAxisX) + 1) < 1e-6) {
            axisXRotateAxis = this._dir.clone();
        }
        if (axisXRotateAxis.length() > 1e-6) {
            // 绕axisX × axisX2，旋转axisX-axisX2角度，即旋转到当前ucs的X轴投影到截面的axisX方向
            this.helpExtrudeSolid._geo.rotate(this.firstPoint as Point, axisXRotateAxis, axisXDeltaAngle);
        }
        if (this.lastExtrudeSolid == null) {
            // 是第一个管道，不用执行以下步骤
            this.helpExtrudeSolid._geo.generateBottomMiddlePoints();
            this.helpExtrudeSolid.updateViewGeometry();
            this.listeners.signals.needRender.dispatch();
            return;
        }
        // 不是第一个管道，需要更新连接旋转体，截取当前管道
        this._tmpAxisDir.copy(this.lastExtrudeSolid._geo.bottomFace.normal);
        // 旋转体的旋转轴方向向量
        this._axisDir.copy(this._tmpAxisDir).cross(this._dir).normalize();
        // 旋转角度
        let angle = this._tmpAxisDir.angleTo(this._dir);
        let value = this._tmpAxisDir.clone().cross(this._dir).angleTo(this._axisDir) * 180 / Math.PI;
        // 处理大于180度的角
        if (value > 90) {
            angle = 2 * Math.PI - angle;
        }
        console.log('angle', angle)
        this.tunnelAngle = angle

        if (angle < 1e-6) {
            // 直管连接，无旋转体，不用执行以下步骤
            if (this.helpRevolveSolid && this.helpRevolveSolid.viewObj && this.helpRevolveSolid.viewObj.parent) {
                this.helpRevolveSolid.viewObj.parent?.remove(this.helpRevolveSolid.viewObj);
            }
            this.dist = 0
            this.helpRevolveSolid = null;
            this.helpExtrudeSolid._geo.generateBottomMiddlePoints();
            this.helpExtrudeSolid.updateViewGeometry();
            this.listeners.signals.needRender.dispatch();
            return;
        }
        // 连接管中心轴与上一段管道的中心轴切点距离连接端点的距离，构造半径是diameter的弧作为连接管中心轴
        this.dist = this.diameter * Math.tan(angle / 2);
        // 管道连接端点距离旋转轴的距离
        let axisDist = this.diameter / (Math.cos(angle / 2));
        this._tmp.copy(this._dir).applyAxisAngle(this._axisDir, Math.PI / 2 - angle / 2).normalize();
        // 旋转轴位置
        this._axisOrigin.copy(this.firstPoint).add(this._tmp.multiplyScalar(axisDist));
        // 旋转轴
        this._axis.set(this._axisOrigin, this._axisDir);
        let botomFace = this.lastExtrudeSolid._geo.bottomFace.clone().move(this._tmpAxisDir, this.lastExtrudeSolid._geo.height - this.dist);
        if (this.helpRevolveSolid == null) {
            this.helpRevolveSolid = new RevolveSolidT(botomFace, this._axis, angle);
            this.extraContext.sceneContext.scene.add(this.helpRevolveSolid.render());
        } else {
            // 旋转体的截面位置是上一段管道截取点的位置
            this.helpRevolveSolid._geo.bottomFace = botomFace;
            // 更新旋转轴
            this.helpRevolveSolid._geo.axis.copy(this._axis);
            // 更新旋转角度
            this.helpRevolveSolid._geo.angle = angle;
            this.helpRevolveSolid._geo.generateRevolveSurfaces();
            this.helpRevolveSolid._geo.generateRevolveArcs();
            this.helpRevolveSolid.updateViewGeometry();
        }
        this.helpRevolveSolid._geo.firstPoint.copy(this.firstPoint)
        this.helpRevolveSolid._geo.secendPoint.copy(this.point_move)

        // 当前管道也截取相同长度
        if (this.dist < this._dir.length()) {
            // this.helpExtrudeSolid._geo.bottomFace.getCenter(this._tmpPoint);
            // console.log("this._tmpPoint.distanceTo(this.firstPoint):", this._tmpPoint.distanceTo(this.firstPoint));
            // this.helpExtrudeSolid._geo.move(this._dir.clone().normalize(), this.dist - this._tmpPoint.distanceTo(this.firstPoint));
            this.helpExtrudeSolid._geo.moveNoMovePoint(this._dir.clone().normalize(), this.dist);
            this.helpExtrudeSolid._geo.height -= this.dist;
            this.helpExtrudeSolid._geo.generateBottomMiddlePoints();
            this.helpExtrudeSolid.updateViewGeometry();
            this.listeners.signals.needRender.dispatch();
        }
    }

    private _tmp = new Vector3();
    private _axisDir = new Vector3();
    private _axisOrigin = new Point();
    private _axis = new StraightLine();
    generateConnectSolid = () => {
        this._tmpAxisDir.copy(this.lastExtrudeSolid._geo.bottomFace.normal);
        let bottomFace = this.lastExtrudeSolid._geo.bottomFace.clone().move(this._tmpAxisDir, this.lastExtrudeSolid._geo.height);
        this._axisOrigin.copy(this.secondPoint);
        // 旋转轴
        this._axis.set(this._axisOrigin, graphicStore.context.ucsContext.getAxisList().axisZ);
        this.helpRevolveSolid = new RevolveSolidT(bottomFace, this._axis, 0);
        this.extraContext.sceneContext.scene.add(this.helpRevolveSolid.render());
        this.listeners.signals.needRender.dispatch();
    }

    generateSolid = (normal: Vector3 = null, basePoint: Vector3, height: number = 0) => {
        // 体的轴线方向
        let lineDir = normal.clone().normalize();
        // 获得截面bottomFace用的是世界坐标系
        let worldOrigin = new Point(0, 0, 0);
        let worldZ = new Vector3(0, 0, 1);
        let bottomFace = this.bottomFace.clone();
        //将截面沿着轴心旋转
        bottomFace.rotate(worldOrigin, worldZ, this.angle || 0);
        bottomFace.axisX.copy(new Vector3(1, 0, 0));
        // 体轴心的偏移
        let offsetDir = new Vector3(this.ex || 0, this.ey || 0, 0);
        let offsetDirNor = offsetDir.clone().normalize();
        // 将体的轴心偏离轴线
        bottomFace.move(offsetDirNor, offsetDir.length());
        let normalRotateAxis = bottomFace.normal.clone().cross(lineDir).normalize();
        let normalDeltaAngle = bottomFace.normal.angleTo(lineDir);
        // 底面旋转至相应的角度
        if (normalRotateAxis.length() > 1e-6) {
            bottomFace.rotate(worldOrigin, normalRotateAxis, normalDeltaAngle);
        }
        // 竖向偏移
        bottomFace.move(lineDir, this.ez);
        // 将截面平移至相应位置
        bottomFace.move(basePoint.clone().normalize(), basePoint.length());
        this.helpExtrudeSolid = new TunnelT(bottomFace, lineDir, height);
        this.helpExtrudeSolid.type = "TunnelT";
        this.helpExtrudeSolid._geo.originPoint.copy(basePoint)
        this.helpExtrudeSolid._geo.diameter = this.diameter
        let newAxisX = this.context.ucsContext.getAxisList().axisX.clone().projectOnPlane(lineDir);
        let axisXRotateAxis = bottomFace.axisX.clone().cross(newAxisX).normalize();
        let axisXDeltaAngle = bottomFace.axisX.angleTo(newAxisX);
        if (Math.abs(bottomFace.axisX.dot(newAxisX) + 1) < 1e-6) {
            axisXRotateAxis = lineDir.clone();
        }
        if (axisXRotateAxis.length() > 1e-6) {
            this.helpExtrudeSolid.rotate(basePoint as Point, axisXRotateAxis, axisXDeltaAngle);
        }
        this.extraContext.sceneContext.scene.add(this.helpExtrudeSolid.render());
        this.listeners.signals.needRender.dispatch();
    }
}